<template>
  <admin-navbar>
    <h1 class="font-bold text-lg">Судалгаа, тайлан</h1>
    <div class="bg-white rounded shadow">
      <div
        class="flex justify-between items-center border-t-4 border-t-blue-500 rounded-t border-b p-3"
      >
        <h6 class="text-base">Тайлан</h6>

        <router-link
          v-if="!isEdit"
          to="/admin/add-report"
          class="bg-blue-500 p-2 text-white text-xs rounded hover:bg-blue-600"
        >
          <font-awesome-icon icon="fa-solid fa-plus" class="mr-1" />
          Тайлан нэмэх
        </router-link>

        <button
          v-else
          @click="closeEdit()"
          class="bg-blue-500 p-2 text-white text-xs rounded hover:bg-blue-600"
        >
          <font-awesome-icon icon="fa-solid fa-angles-left" class="mr-1" />
          Буцах
        </button>
      </div>

      <div v-if="!isEdit" class="p-3 space-y-5">
        <div class="flex justify-between items-center text-xs">
          <div class="space-x-3">
            <span>Хуудас бүрт:</span>
            <select
              name="pagePerPage"
              id="pagePerPage"
              class="border rounded p-1 text-blue-500"
              v-model="dataPerPage"
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
            </select>
          </div>

          <span>Нийт тайлангийн тоо: {{ totalCount }} </span>

          <div class="flex items-center gap-3">
            <span>Нийт хуудас: {{ totalPages }} </span>
            <PaginationCart
              :totalPages="totalPages"
              :modelValue="currentPage"
              :style="{
                color: 'blue-500',
                height: 7,
                width: 7,
                fontSize: 'xs',
              }"
              @page-changed="onPageChanged"
            />
          </div>
        </div>

        <div v-if="reports.length > 0">
          <div class="overflow-auto">
            <table
              class="w-full border rounded-lg divide-y divide-gray-200 text-xs"
            >
              <thead class="bg-gray-100">
                <tr>
                  <th scope="col" class="px-6 py-3">Үйлдэл</th>
                  <th scope="col" class="px-6 py-3">Гарчиг</th>
                  <th scope="col" class="px-6 py-3">Гарчиг /англи/</th>
                  <th scope="col" class="px-6 py-3">Зураг</th>
                  <th scope="col" class="px-6 py-3">Файл</th>
                  <th scope="col" class="px-6 py-3">Нийтлэсэн огноо</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="report in reports"
                  :key="report.id"
                  class="border-b border-gray-300"
                >
                  <td class="px-6 py-4">
                    <div class="flex gap-2">
                      <button
                        @click="editReport(report)"
                        class="bg-blue-500 text-white px-2 py-1 rounded text-xs"
                      >
                        Засварлах
                      </button>
                      <button
                        @click="deleteReport(report.id)"
                        class="bg-red-500 text-white px-2 py-1 rounded text-xs"
                      >
                        Устгах
                      </button>
                    </div>
                  </td>
                  <td class="px-6 py-4">{{ report.title }}</td>
                  <td class="px-6 py-4">{{ report.title_en }}</td>
                  <td class="px-6 py-4">
                    <img
                      v-if="report.image"
                      :src="report.image"
                      alt="Зураг"
                      class="h-12 w-auto object-cover"
                    />
                  </td>
                  <td class="px-6 py-4">
                    <a
                      v-if="report.pdf"
                      :href="report.pdf"
                      target="_blank"
                      class="text-blue-500 underline"
                    >
                      Файл харах
                    </a>
                  </td>
                  <td class="px-6 py-4">{{ report.created_at }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div
          v-else-if="reports.length === 0"
          class="flex justify-center items-center h-[60vh]"
        >
          Жагсаалт хоосон байна
        </div>

        <div v-else class="flex justify-center items-center h-[60vh]">
          <SpinLoading :color="'rgb(59 130 246)'" />
        </div>
      </div>

      <MMEditReport v-else :data="selectedReport" @fetchData="fetchReports()" />
    </div>
  </admin-navbar>
</template>

<script>
import AdminNavbar from "@/components/admin/AdminNavbar.vue";
import adminInstance from "@/lib/adminInstance";
import { onMounted, ref, watch } from "vue";
import PaginationCart from "@/components/ui/PaginationCart.vue";
import SpinLoading from "@/components/ui/SpinLoading.vue";
import MMEditReport from "@/containers/MiningMongolia/admin/MMEditReport.vue";

export default {
  name: "MMAdminReport",
  components: {
    AdminNavbar,
    PaginationCart,
    SpinLoading,
    MMEditReport,
  },
  setup() {
    const reports = ref([]);
    const selectedReport = ref(null);
    const isLoading = ref(false);

    const dataPerPage = ref(5);
    const totalCount = ref(null);
    const totalPages = ref(1);
    const currentPage = ref(1);

    const isEdit = ref(false);

    const fetchReports = async () => {
      try {
        const res = await adminInstance.get(
          `/get/reports?page=${currentPage.value}&limit=${dataPerPage.value}`
        );
        if (res.status === 200) {
          reports.value = res.data.reports;
          totalCount.value = res.data.total;
          totalPages.value = res.data.totalPages;
        }
      } catch (err) {
        return err;
      }
    };

    onMounted(() => {
      fetchReports();
    });

    watch([() => dataPerPage.value], () => {
      currentPage.value = 1;
      fetchReports();
    });

    const onPageChanged = (page) => {
      currentPage.value = page;
      fetchReports();
    };

    const editReport = (report) => {
      selectedReport.value = report;
      isEdit.value = true;
    };

    const deleteReport = async (reportId) => {
      if (!window.confirm(`Тайланг устгах уу?`)) {
        return;
      }

      try {
        const res = await adminInstance.delete(`/delete/report?id=${reportId}`);
        if (res.status === 200) {
          reports.value = reports.value.filter(
            (report) => report.id !== reportId
          );
          totalCount.value--;
        }
      } catch (err) {
        console.error(err);
      }
    };

    const closeEdit = () => {
      isEdit.value = false;
    };

    return {
      reports,
      isLoading,
      dataPerPage,
      totalCount,
      totalPages,
      currentPage,
      onPageChanged,
      editReport,
      deleteReport,
      selectedReport,
      isEdit,
      closeEdit,
      fetchReports,
    };
  },
};
</script>
